import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/client";
import {
    Receipt,
    Coins,
    AlertOctagon,
    AlertTriangle,
    DoorOpen,
    Info,
} from "lucide-react";

const DURATION_LABELS = {
    "15m": "15 minutes", "30m": "30 minutes",
    "1h": "1 hour", "3h": "3 hours", "6h": "6 hours", "12h": "12 hours", "24h": "24 hours",
    "15d": "15 days", "1mo": "1 month", "1yr": "1 year",
};
const SHORT_DURATION_KEYS = new Set(["15m", "30m"]);

// Static fallback if /api/swaps/protocol isn't reachable (keeps the page usable
// without a network round-trip).
const FALLBACK_ENTRY_PCT = {
    "15m": 0.00025, "30m": 0.0005, "1h": 0.001, "3h": 0.0015, "6h": 0.0015,
    "12h": 0.0015, "24h": 0.0015, "15d": 0.002, "1mo": 0.0035, "1yr": 0.005,
};
const FALLBACK_SETTLE_PCT = {
    "15m": 0, "30m": 0, "1h": 0, "3h": 0, "6h": 0, "12h": 0, "24h": 0,
    "15d": 0.001, "1mo": 0.001, "1yr": 0.001,
};

function buildTiers(entryPct, settlePct) {
    return Object.keys(DURATION_LABELS).map((k) => {
        const e = entryPct[k] ?? 0;
        const s = settlePct[k] ?? 0;
        const minPct = SHORT_DURATION_KEYS.has(k) ? "1%" : "2%";
        return {
            d: DURATION_LABELS[k], k,
            f: `${(e * 100).toFixed(e < 0.001 ? 3 : 2)}%`,
            ex: `$${(e * 10000).toFixed(2)}`,
            note: s > 0 ? `+ ${(s * 100).toFixed(2)}% settlement` : `min margin ${minPct}`,
        };
    });
}

const SECONDARY = [
    {
        title: "Settlement fee",
        value: "0.10% per side",
        applies: "15d / 1mo / 1yr only",
        body: "Charged at expiry — both the winner and the loser pay 0.10% of notional. Short and medium swaps (15 min → 24 h) have no settlement fee — only the entry fee at match.",
        icon: Coins,
        accent: "cyan",
    },
    {
        title: "Liquidation fee",
        value: "2% of notional · cap",
        applies: "auto-close at 95% margin depletion",
        body: "If your position is auto-closed at 95% margin depletion, the protocol sweeps whatever remains of your margin (max 5%), but the fee is hard-capped at 2% of notional. Anything above that cap is refunded to you.",
        icon: AlertOctagon,
        accent: "rose",
    },
    {
        title: "Early-exit penalty",
        value: "2% + 1% of your margin",
        applies: "voluntary close before expiry",
        body: "2% of your margin goes to the counterparty as compensation, 1% to Verslan. Your current mark-to-market PnL is settled normally on top of the penalty.",
        icon: DoorOpen,
        accent: "amber",
    },
];

const ACCENT = {
    cyan: { border: "border-cyan-500/25", icon: "text-cyan-400", value: "text-cyan-300" },
    rose: { border: "border-rose-500/25", icon: "text-rose-400", value: "text-rose-300" },
    amber: { border: "border-amber-500/25", icon: "text-amber-400", value: "text-amber-300" },
};

export default function Fees() {
    const { user } = useAuth();
    const ctaHref = user ? "/terminal" : "/auth?mode=register";
    const [tiers, setTiers] = useState(() => buildTiers(FALLBACK_ENTRY_PCT, FALLBACK_SETTLE_PCT));
    useEffect(() => {
        let alive = true;
        api.get("/swaps/protocol").then((r) => {
            if (!alive) return;
            const e = r?.data?.entry_fee_pct || FALLBACK_ENTRY_PCT;
            const s = r?.data?.settlement_fee_pct || FALLBACK_SETTLE_PCT;
            setTiers(buildTiers(e, s));
        }).catch(() => {});
        return () => { alive = false; };
    }, []);
    const ENTRY_TIERS = tiers;

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)]">
            {/* HERO */}
            <section className="border-b border-[#1F1F24] px-6 lg:px-12 py-16 lg:py-20">
                <div className="max-w-[1280px] mx-auto">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">
                        Fee schedule · transparent · on-chain by design
                    </div>
                    <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05]">
                        Every fee, listed.
                    </h1>
                    <p className="mt-5 max-w-2xl text-zinc-400 leading-relaxed">
                        Verslan BUBE charges a single entry fee at match, tiered by duration
                        from <span className="text-zinc-100 font-mono">0.025%</span> to{" "}
                        <span className="text-zinc-100 font-mono">0.50%</span> of notional.
                        Long-duration swaps (15 days, 1 month, 1 year) add a{" "}
                        <span className="text-cyan-300 font-mono">0.10%</span> settlement fee
                        at expiry, paid by the losing side. No funding rates. No spread games.
                        No surprise debits.
                    </p>

                    {/* Headline stats */}
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {[
                            { k: "Entry range", v: "0.025% – 0.50%", s: "tiered by duration" },
                            { k: "Settlement", v: "0.10%", s: "15d / 1mo / 1yr only" },
                            { k: "Liquidation cap", v: "2% notional", s: "excess refunded" },
                            { k: "Early-exit", v: "2% + 1%", s: "counter + Verslan" },
                        ].map((s) => (
                            <div
                                key={s.k}
                                data-testid={`fees-summary-${s.k.toLowerCase().replace(/\s+/g, "-")}`}
                                className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5"
                            >
                                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                                    {s.k}
                                </div>
                                <div className="mt-2 text-2xl font-medium tracking-tight font-mono text-zinc-100">
                                    {s.v}
                                </div>
                                <div className="mt-1 text-[11px] text-zinc-500">{s.s}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ENTRY TIERS TABLE */}
            <section className="border-b border-[#1F1F24] px-6 lg:px-12 py-16">
                <div className="max-w-[1280px] mx-auto">
                    <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-cyan-400" />
                        <div className="text-sm font-medium tracking-tight">
                            Entry fee · charged once at match, deducted from both sides
                        </div>
                    </div>
                    <p className="mt-2 text-xs text-zinc-500 max-w-2xl leading-relaxed">
                        The example column assumes a notional of{" "}
                        <span className="font-mono text-zinc-300">$10,000</span>. The fee scales
                        linearly with your notional. Both bull and bear pay the same entry fee.
                    </p>

                    <div className="mt-6 bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-x-auto">
                        <table className="w-full font-mono text-sm min-w-[640px]" data-testid="fees-table">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-[#1F1F24]">
                                    <th className="text-left px-5 py-3 font-normal">Duration</th>
                                    <th className="text-left px-5 py-3 font-normal">Tier</th>
                                    <th className="text-right px-5 py-3 font-normal">Entry fee</th>
                                    <th className="text-right px-5 py-3 font-normal">On $10,000</th>
                                    <th className="text-right px-5 py-3 font-normal">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ENTRY_TIERS.map((t) => (
                                    <tr
                                        key={t.d}
                                        data-testid={`fees-row-${t.k}`}
                                        className="border-t border-[#1F1F24]/60 hover:bg-[#0F0F12]/60 transition-colors"
                                    >
                                        <td className="px-5 py-3 text-zinc-100">{t.d}</td>
                                        <td className="px-5 py-3 text-zinc-500">{t.k}</td>
                                        <td className="px-5 py-3 text-right text-cyan-300">{t.f}</td>
                                        <td className="px-5 py-3 text-right text-zinc-300">{t.ex}</td>
                                        <td className="px-5 py-3 text-right text-[11px] text-zinc-500">
                                            {t.note}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
                        <Info className="w-3.5 h-3.5 text-zinc-600 mt-0.5 shrink-0" />
                        <span>
                            Short tenors (15m, 30m) allow a minimum margin of{" "}
                            <span className="text-zinc-300">1%</span> of notional (max 100×
                            leverage). All other durations require a minimum margin of{" "}
                            <span className="text-zinc-300">2%</span> of notional (max 50×
                            leverage). Asymmetric margins between the two sides are allowed.
                        </span>
                    </div>
                </div>
            </section>

            {/* SECONDARY FEES */}
            <section className="border-b border-[#1F1F24] px-6 lg:px-12 py-16">
                <div className="max-w-[1280px] mx-auto">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">
                        Other charges · only triggered in specific events
                    </div>
                    <h2 className="mt-3 text-2xl lg:text-3xl font-medium tracking-tight">
                        Settlement, liquidation & early-exit.
                    </h2>

                    <div className="mt-8 grid lg:grid-cols-3 gap-2">
                        {SECONDARY.map((s) => {
                            const Icon = s.icon;
                            const a = ACCENT[s.accent];
                            return (
                                <div
                                    key={s.title}
                                    data-testid={`fees-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`bg-[#0A0A0C] border ${a.border} rounded-sm p-6`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Icon className={`w-4 h-4 ${a.icon}`} />
                                        <div className="text-sm font-medium tracking-tight">
                                            {s.title}
                                        </div>
                                    </div>
                                    <div
                                        className={`mt-4 text-2xl font-mono tracking-tight ${a.value}`}
                                    >
                                        {s.value}
                                    </div>
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">
                                        Applies · {s.applies}
                                    </div>
                                    <p className="mt-4 text-xs text-zinc-400 leading-relaxed">
                                        {s.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHAT YOU DON'T PAY */}
            <section className="border-b border-[#1F1F24] px-6 lg:px-12 py-16">
                <div className="max-w-[1280px] mx-auto">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">
                        Zero hidden costs
                    </div>
                    <h2 className="mt-3 text-2xl lg:text-3xl font-medium tracking-tight">
                        What you don't pay.
                    </h2>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {[
                            ["Funding rates", "BUBE is bilateral, not perpetual. No 8h funding skim."],
                            ["Spread", "Match price is the Hyperliquid mid — no maker/taker spread."],
                            ["Borrow fees", "No leverage borrowing. Margin is your maximum loss."],
                            ["Withdrawal fees", "Move USDC in and out without protocol charges."],
                        ].map(([t, b]) => (
                            <div
                                key={t}
                                className="bg-[#0A0A0C] border border-emerald-500/15 rounded-sm p-5"
                            >
                                <div className="text-sm font-medium tracking-tight text-emerald-300">
                                    {t}
                                </div>
                                <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 lg:px-12 py-20">
                <div className="max-w-[1280px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <h3 className="text-2xl font-medium tracking-tight">
                            Ready to trade with bounded exposure?
                        </h3>
                        <p className="mt-2 text-sm text-zinc-400">
                            Every fee above is what you'll see in the swap ticket — to the cent.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/how-it-works"
                            data-testid="fees-cta-how"
                            className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-zinc-100 px-4 py-2.5 border border-[#1F1F24] rounded-sm"
                        >
                            How BUBE works
                        </Link>
                        <Link
                            to={ctaHref}
                            data-testid="fees-cta-launch"
                            className="text-[11px] uppercase tracking-[0.18em] text-black bg-cyan-400 hover:bg-cyan-300 px-4 py-2.5 rounded-sm font-semibold"
                        >
                            {user ? "Open terminal →" : "Launch testnet →"}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
